import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useTheme } from '@/App';
import DashboardLayout from '@/components/layout/DashboardLayout';
import {
  Users, Calendar as CalendarIcon, AlertCircle, Search, X, Save, Trash2,
  StickyNote, RefreshCw, PhoneCall, ChevronLeft, ChevronRight, CheckCircle2,
  Moon, Sun, BarChart2, MessageCircle, Share2, MoreHorizontal, ArrowUpRight,
  ArrowDownRight, Edit3, CalendarDays, Mail, Phone, ExternalLink, Clock,
  Filter, Check, ChevronDown, UserCheck, ShieldCheck, Sparkles, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateReadable = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const getFollowupStatus = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  const now = new Date();
  target.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  const diff = Math.round((target - now) / (1000 * 60 * 60 * 24));
  
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, type: 'overdue', diff, dateFormatted: formatDateReadable(dateStr) };
  if (diff === 0) return { label: 'Due today', type: 'today', diff: 0, dateFormatted: formatDateReadable(dateStr) };
  if (diff === 1) return { label: 'Tomorrow', type: 'soon', diff: 1, dateFormatted: formatDateReadable(dateStr) };
  if (diff <= 7) return { label: `In ${diff}d`, type: 'soon', diff, dateFormatted: formatDateReadable(dateStr) };
  return { label: `In ${diff}d`, type: 'future', diff, dateFormatted: formatDateReadable(dateStr) };
};

const getRoleBadge = (role, isLight) => {
  const r = (role || 'student').toLowerCase();
  if (r === 'admin' || r === 'superadmin') {
    return isLight 
      ? 'bg-rose-100 text-rose-700 border-rose-200' 
      : 'bg-rose-500/20 text-rose-300 border-rose-500/30';
  }
  if (r === 'management') {
    return isLight 
      ? 'bg-indigo-100 text-indigo-700 border-indigo-200' 
      : 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30';
  }
  if (r === 'mentor') {
    return isLight 
      ? 'bg-purple-100 text-purple-700 border-purple-200' 
      : 'bg-purple-500/20 text-purple-300 border-purple-500/30';
  }
  if (r === 'tech' || r === 'tech team') {
    return isLight 
      ? 'bg-blue-100 text-blue-700 border-blue-200' 
      : 'bg-blue-500/20 text-blue-300 border-blue-500/30';
  }
  if (r === 'counselor') {
    return isLight 
      ? 'bg-amber-100 text-amber-700 border-amber-200' 
      : 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  }
  return isLight 
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
    : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
};

const QUICK_DAYS = [
  { label: '+1 Day', days: 1 },
  { label: '+2 Days', days: 2 },
  { label: '+3 Days', days: 3 },
  { label: '+5 Days', days: 5 },
  { label: '+1 Week', days: 7 },
  { label: '+2 Weeks', days: 14 },
  { label: '+1 Month', days: 30 }
];

function FollowUpPanel({ user, onClose, onSave, theme }) {
  const existing = user?.followup;
  const [notes, setNotes] = useState(existing?.notes || '');
  const [date, setDate] = useState(existing?.followup_date ? existing.followup_date.split('T')[0] : '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => { 
    requestAnimationFrame(() => setIsOpen(true)); 
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 280);
  };

  const setQuickDays = (d) => {
    const dt = new Date();
    dt.setDate(dt.getDate() + d);
    setDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/mentor/followup`, { 
        user_id: user.user_id, 
        notes: notes.trim(), 
        followup_date: date || null 
      }, { withCredentials: true });
      toast.success('Follow-up record saved successfully');
      onSave();
      handleClose();
    } catch { 
      toast.error('Failed to save follow-up'); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to clear this follow-up schedule and notes?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/mentor/followup/${user.user_id}`, { withCredentials: true });
      toast.success('Follow-up cleared');
      onSave();
      handleClose();
    } catch { 
      toast.error('Failed to clear follow-up'); 
    } finally { 
      setDeleting(false); 
    }
  };

  const isLight = theme === 'light';
  const cleanPhone = (user.phone || '').replace(/[^0-9+]/g, '');
  const waNumber = cleanPhone.startsWith('+') ? cleanPhone.slice(1) : (cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone);
  
  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} 
        onClick={handleClose} 
      />
      
      {/* Drawer */}
      <div 
        className={`relative w-full max-w-lg h-full shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isLight ? 'bg-white text-slate-900 border-l border-slate-200' : 'bg-[#0D0F17] text-white border-l border-white/[0.08]'}`}
        style={{ boxShadow: isLight ? '-15px 0 45px rgba(0,0,0,0.12)' : '-20px 0 60px rgba(0,0,0,0.85)' }}
      >
        {/* Header */}
        <div className={`p-6 border-b flex items-center justify-between relative overflow-hidden ${
          isLight ? 'border-slate-100 bg-slate-50/80' : 'border-white/[0.06] bg-white/[0.02]'
        }`}>
          <div className="flex items-center gap-4 relative z-10">
            <Avatar className="w-14 h-14 border-2 border-cyan-500/30 shadow-md">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className={isLight ? 'bg-indigo-100 text-indigo-700 font-bold text-lg' : 'bg-white/10 text-cyan-400 font-bold text-lg'}>
                {user.name?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h3 className={`text-lg font-bold font-outfit ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  {user.name || 'Unnamed User'}
                </h3>
                <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full uppercase tracking-wider border ${getRoleBadge(user.role, isLight)}`}>
                  {user.role || 'Student'}
                </span>
              </div>
              <p className={`text-xs font-mono mt-0.5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                {user.email || 'No email provided'}
              </p>
              <div className="flex items-center gap-3 mt-1">
                {user.phone && (
                  <p className={`text-xs font-mono flex items-center gap-1 ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
                    <Phone className="w-3 h-3 text-emerald-500" /> {user.phone}
                  </p>
                )}
                {user.city && (
                  <p className={`text-xs flex items-center gap-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                    <MapPin className="w-3 h-3 text-indigo-400" /> {user.city}
                  </p>
                )}
              </div>
            </div>
          </div>
          <button 
            onClick={handleClose} 
            className={`p-2 rounded-full transition-colors ${
              isLight ? 'bg-white hover:bg-slate-200 text-slate-600 border border-slate-200' : 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Communication Actions Bar */}
        <div className={`px-6 py-3 border-b flex items-center gap-2 ${
          isLight ? 'bg-white border-slate-100' : 'bg-black/20 border-white/[0.04]'
        }`}>
          {user.email && (
            <a 
              href={`mailto:${user.email}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                isLight 
                  ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' 
                  : 'bg-white/5 hover:bg-white/10 text-slate-200 border border-white/5'
              }`}
            >
              <Mail className="w-3.5 h-3.5 text-blue-500" /> Send Email
            </a>
          )}
          {user.phone && (
            <>
              <a 
                href={`tel:${cleanPhone}`} 
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                  isLight 
                    ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700' 
                    : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/20'
                }`}
              >
                <PhoneCall className="w-3.5 h-3.5 text-emerald-500" /> Direct Call
              </a>
              <a 
                href={`https://wa.me/${waNumber}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                  isLight 
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm' 
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm'
                }`}
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            </>
          )}
        </div>

        {/* Drawer Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y p-6 space-y-6">
          {/* Notes Input */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${
                isLight ? 'text-slate-700' : 'text-slate-300'
              }`}>
                <StickyNote className="w-4 h-4 text-amber-500" /> Mentorship Notes & Interaction History
              </label>
              <span className={`text-[11px] ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
                {notes.length} chars
              </span>
            </div>
            <textarea 
              value={notes} 
              onChange={e => setNotes(e.target.value)} 
              placeholder="e.g. Student requested guidance on Options buying vs selling. Target scheduled to review Dhan trading journal and risk metrics next Tuesday..." 
              rows={6} 
              className={`w-full rounded-2xl px-4 py-3.5 text-sm resize-none outline-none transition-all shadow-sm border ${
                isLight 
                  ? 'bg-slate-50 hover:bg-white focus:bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100' 
                  : 'bg-black/40 hover:bg-black/60 focus:bg-black/60 border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30'
              }`}
            />
          </div>

          {/* Schedule Date */}
          <div className="space-y-3">
            <label className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${
              isLight ? 'text-slate-700' : 'text-slate-300'
            }`}>
              <CalendarIcon className="w-4 h-4 text-cyan-500" /> Schedule Next Follow-up Date
            </label>
            
            <div className="relative">
              <input 
                type="date" 
                value={date} 
                min={todayStr()} 
                onChange={e => setDate(e.target.value)} 
                className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition-all border font-medium ${
                  isLight 
                    ? 'bg-slate-50 border-slate-200 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 [color-scheme:light]' 
                    : 'bg-black/40 border-white/10 text-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 [color-scheme:dark]'
                }`}
              />
            </div>

            {/* Quick Presets */}
            <div>
              <p className={`text-[11px] font-medium mb-2 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                Quick date presets:
              </p>
              <div className="flex flex-wrap gap-2">
                {QUICK_DAYS.map(item => (
                  <button 
                    key={item.days} 
                    type="button"
                    onClick={() => setQuickDays(item.days)} 
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
                      isLight 
                        ? 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600' 
                        : 'bg-white/[0.03] border-white/[0.08] text-slate-300 hover:bg-cyan-500/10 hover:border-cyan-500/40 hover:text-cyan-300'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                {date && (
                  <button 
                    type="button"
                    onClick={() => setDate('')} 
                    className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all text-red-500 hover:bg-red-500/10 border border-transparent hover:border-red-500/20`}
                  >
                    Clear Date
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className={`p-6 border-t flex items-center gap-3 ${
          isLight ? 'bg-slate-50 border-slate-200' : 'bg-[#090A10] border-white/[0.06]'
        }`}>
          <Button 
            onClick={handleSave} 
            disabled={saving} 
            className={`flex-1 h-12 rounded-xl font-bold font-outfit text-white shadow-lg transition-all hover:opacity-95 ${
              isLight 
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-indigo-500/25' 
                : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-[0_0_20px_rgba(0,240,255,0.3)]'
            }`}
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} 
            Save Follow-up
          </Button>

          {existing && (
            <Button 
              onClick={handleDelete} 
              disabled={deleting} 
              variant="outline" 
              className={`h-12 px-4 rounded-xl font-medium transition-all ${
                isLight 
                  ? 'border-red-200 bg-red-50 hover:bg-red-100 text-red-600' 
                  : 'border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-400'
              }`}
              title="Delete this follow-up record"
            >
              {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          )}

          <Button 
            onClick={handleClose} 
            variant="ghost" 
            className={`h-12 px-4 rounded-xl font-medium ${
              isLight ? 'text-slate-600 hover:bg-slate-200' : 'text-slate-400 hover:bg-white/5'
            }`}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MentorCRMPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'soon', 'overdue', 'unscheduled'
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'name', 'date'
  const [selectedUser, setSelectedUser] = useState(null);
  
  const { theme, setTheme } = useTheme();
  const isLight = theme === 'light';

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mentor/users`, { withCredentials: true });
      const rawUsers = Array.isArray(res.data) ? res.data : [];
      const officialStaff = [
        "sachin@jamadhan.in", "tanmay@jamadhan.in", "yuvraj@jamadhan.in",
        "kiran@jamadhan.in", "renu@wegotem.in", "trilok@jamadhan.in",
        "deepaksethia@jamadhan.in", "admin@jamadhan.in"
      ];
      const studentsOnly = rawUsers.filter(u => 
        !u.is_team_member &&
        !officialStaff.includes(u.email?.toLowerCase()) &&
        !['admin', 'tech', 'team_member', 'mentor', 'counselor', 'management'].includes(u.role) &&
        !u.user_id?.startsWith('user_team_')
      );
      setUsers(studentsOnly);
    } catch (err) { 
      toast.error('Failed to load mentor users directory'); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    fetchUsers(); 
  }, [fetchUsers]);

  // Categorize and compute stats
  const { overdueUsers, todayUsers, soonUsers, unscheduledUsers } = useMemo(() => {
    const overdue = [];
    const today = [];
    const soon = [];
    const unscheduled = [];

    users.forEach(u => {
      const status = getFollowupStatus(u.followup?.followup_date);
      const userWithStatus = { ...u, statusInfo: status };

      if (!status) {
        unscheduled.push(userWithStatus);
      } else if (status.type === 'overdue') {
        overdue.push(userWithStatus);
      } else if (status.type === 'today') {
        today.push(userWithStatus);
      } else {
        soon.push(userWithStatus);
      }
    });

    // Sort upcoming by earliest date
    soon.sort((a, b) => new Date(a.followup.followup_date) - new Date(b.followup.followup_date));
    overdue.sort((a, b) => new Date(a.followup.followup_date) - new Date(b.followup.followup_date));

    return {
      overdueUsers: overdue,
      todayUsers: today,
      soonUsers: soon,
      unscheduledUsers: unscheduled
    };
  }, [users]);

  // Filtered & Sorted users for table display
  const displayedUsers = useMemo(() => {
    let list = [];
    if (activeTab === 'today') list = [...todayUsers];
    else if (activeTab === 'soon') list = [...soonUsers];
    else if (activeTab === 'overdue') list = [...overdueUsers];
    else if (activeTab === 'unscheduled') list = [...unscheduledUsers];
    else list = users.map(u => ({ ...u, statusInfo: getFollowupStatus(u.followup?.followup_date) }));

    // Search query filter across name, email, phone, city, role, user_id, notes
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(u => 
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.phone && u.phone.toLowerCase().includes(q)) ||
        (u.city && u.city.toLowerCase().includes(q)) ||
        (u.role && u.role.toLowerCase().includes(q)) ||
        (u.user_id && u.user_id.toLowerCase().includes(q)) ||
        (u.followup?.notes && u.followup.notes.toLowerCase().includes(q))
      );
    }

    // Apply Sorting
    if (sortBy === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'recent') {
      list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === 'date') {
      // Overdue first, then today, then soonest upcoming, then unscheduled
      list.sort((a, b) => {
        const da = a.followup?.followup_date ? new Date(a.followup.followup_date).getTime() : Infinity;
        const db = b.followup?.followup_date ? new Date(b.followup.followup_date).getTime() : Infinity;
        return da - db;
      });
    }

    return list;
  }, [activeTab, search, sortBy, users, overdueUsers, todayUsers, soonUsers, unscheduledUsers]);

  const totalFollowupsCount = useMemo(() => {
    return users.filter(u => !!u.followup?.followup_date || !!u.followup?.notes).length;
  }, [users]);

  return (
    <DashboardLayout>
      <div className={`min-h-[calc(100vh-64px)] transition-colors duration-300 ${
        isLight ? 'bg-slate-50 text-slate-900' : 'bg-[#05060A] text-white'
      }`}>
        <div className="max-w-[1500px] mx-auto p-4 md:p-8 space-y-8">
          
          {/* Top Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className={`text-2xl md:text-3xl font-extrabold font-outfit tracking-tight ${
                  isLight ? 'text-slate-900' : 'text-white'
                }`}>
                  Mentor Hub & CRM
                </h1>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                  isLight 
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                    : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                }`}>
                  {users.length} Total Users
                </span>
              </div>
              <p className={`text-sm mt-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                Manage student follow-ups, scheduled mentoring calls, and conversation history.
              </p>
            </div>
            
            {/* Header Right Tools */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Search Box */}
              <div className="relative w-full sm:w-72">
                <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-slate-400' : 'text-slate-500'}`} />
                <input 
                  type="text" 
                  placeholder="Search name, email, note, city..." 
                  value={search} 
                  onChange={e => setSearch(e.target.value)} 
                  className={`w-full rounded-full pl-10 pr-9 py-2.5 text-sm outline-none transition-all ${
                    isLight 
                      ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 shadow-sm' 
                      : 'bg-white/[0.05] border border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30'
                  }`}
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')} 
                    className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-white/10 ${
                      isLight ? 'text-slate-400 hover:text-slate-600' : 'text-slate-500 hover:text-white'
                    }`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Theme Toggle */}
              <button 
                onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')} 
                className={`p-2.5 rounded-full transition-all border ${
                  isLight 
                    ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100 shadow-sm' 
                    : 'bg-white/[0.06] border-white/10 text-slate-200 hover:bg-white/[0.12]'
                }`}
                title="Toggle Theme"
              >
                {isLight ? <Moon className="w-5 h-5 text-indigo-600" /> : <Sun className="w-5 h-5 text-amber-400" />}
              </button>

              {/* Refresh Button */}
              <button 
                onClick={fetchUsers}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm ${
                  isLight 
                    ? 'bg-slate-900 text-white hover:bg-slate-800 active:scale-95' 
                    : 'bg-white text-slate-900 hover:bg-slate-100 active:scale-95'
                }`}
              >
                <span>Refresh</span>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* KPI Analytics Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            {/* Card 1: Banner Card (Total Follow-ups & Pipeline) */}
            <div 
              className={`md:col-span-5 rounded-3xl p-6 md:p-7 relative overflow-hidden shadow-lg transform transition-transform hover:-translate-y-0.5 text-white ${
                isLight 
                  ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-800 shadow-slate-900/10' 
                  : 'bg-gradient-to-br from-[#12162B] via-[#101322] to-[#090B14] border border-cyan-500/30 shadow-[0_0_30px_rgba(0,240,255,0.06)]'
              }`}
            >
              {/* Subtle background glow */}
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 rounded-lg bg-white/20 backdrop-blur-md">
                      <Sparkles className="w-4 h-4 text-white" />
                    </span>
                    <p className="text-white/80 font-medium text-xs font-outfit uppercase tracking-wider">
                      Active Follow-up Pipeline
                    </p>
                  </div>
                  <h2 className="text-5xl font-black mt-2 tracking-tight text-white">
                    {totalFollowupsCount}
                  </h2>
                  <p className="text-xs text-white/70 mt-1">Users with CRM schedules or interaction notes</p>
                </div>

                <button 
                  onClick={() => { setActiveTab('all'); setSearch(''); }}
                  className="bg-white/15 hover:bg-white/25 transition-colors p-2.5 rounded-full backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1"
                  title="View all users"
                >
                  <Users className="w-4 h-4" />
                </button>
              </div>

              {/* Sparkline curve */}
              <div className="h-16 w-full mt-4 -mb-2">
                <svg viewBox="0 0 100 25" preserveAspectRatio="none" className="w-full h-full opacity-60 drop-shadow">
                  <path d="M0 20 Q20 5 40 16 T80 8 T100 18 L100 25 L0 25 Z" fill="rgba(255,255,255,0.12)" />
                  <path d="M0 20 Q20 5 40 16 T80 8 T100 18" fill="none" stroke="white" strokeWidth="1.8" />
                </svg>
              </div>

              {/* Sub metrics footer */}
              <div className="relative z-10 grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/20 text-white text-xs">
                <div 
                  onClick={() => setActiveTab('today')}
                  className="cursor-pointer hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <p className="text-white/70 text-[11px]">Due Today</p>
                  <p className="text-lg font-bold text-amber-300">{todayUsers.length}</p>
                </div>
                <div 
                  onClick={() => setActiveTab('overdue')}
                  className="cursor-pointer hover:bg-white/10 p-2 rounded-xl transition-all border-x border-white/15"
                >
                  <p className="text-white/70 text-[11px]">Overdue</p>
                  <p className="text-lg font-bold text-red-300">{overdueUsers.length}</p>
                </div>
                <div 
                  onClick={() => setActiveTab('soon')}
                  className="cursor-pointer hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <p className="text-white/70 text-[11px]">Upcoming</p>
                  <p className="text-lg font-bold text-cyan-300">{soonUsers.length}</p>
                </div>
              </div>
            </div>

            {/* Card 2: Task Health & Priority Donut */}
            <div className={`md:col-span-3 rounded-3xl p-6 flex flex-col justify-between shadow-sm border transition-all ${
              isLight ? 'bg-white border-slate-200' : 'bg-[#0D0F17] border-white/[0.06]'
            }`}>
              <div className="flex justify-between items-center">
                <span className={`text-xs font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  Follow-up Health
                </span>
                <span className={`p-1.5 rounded-lg ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/5 text-slate-300'}`}>
                  <AlertCircle className="w-4 h-4" />
                </span>
              </div>

              <div className="flex items-center justify-between my-2">
                <div>
                  <h2 className={`text-3xl font-black tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
                    {overdueUsers.length + todayUsers.length}
                  </h2>
                  <p className={`text-xs font-medium mt-0.5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                    Pending Attention
                  </p>

                  <div className="mt-4 space-y-2">
                    <div 
                      onClick={() => setActiveTab('overdue')}
                      className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className={`text-xs font-semibold ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                        Overdue ({overdueUsers.length})
                      </span>
                    </div>
                    <div 
                      onClick={() => setActiveTab('today')}
                      className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                      <span className={`text-xs font-semibold ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                        Today ({todayUsers.length})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Donut graphic */}
                <div className="relative w-24 h-24">
                  <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                    <circle cx="50" cy="50" r="38" fill="none" stroke={isLight ? "#E2E8F0" : "#1E2230"} strokeWidth="12" />
                    {overdueUsers.length + todayUsers.length > 0 && (
                      <circle 
                        cx="50" 
                        cy="50" 
                        r="38" 
                        fill="none" 
                        stroke="#EF4444" 
                        strokeWidth="12" 
                        strokeDasharray="238.76" 
                        strokeDashoffset={238.76 * (1 - (overdueUsers.length / Math.max(1, overdueUsers.length + todayUsers.length)))} 
                        strokeLinecap="round" 
                      />
                    )}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>
                      {overdueUsers.length > 0 ? `${overdueUsers.length} Over` : 'Clean'}
                    </span>
                  </div>
                </div>
              </div>

              <div className={`pt-3 border-t text-[11px] ${isLight ? 'border-slate-100 text-slate-500' : 'border-white/[0.04] text-slate-400'}`}>
                {overdueUsers.length === 0 ? '✨ Great job! Zero overdue follow-ups.' : '⚠️ Resolve overdue student contacts promptly.'}
              </div>
            </div>

            {/* Card 3: Due Today & Quick Actions */}
            <div className={`md:col-span-4 grid grid-rows-2 gap-4`}>
              {/* Due Today Mini Card */}
              <div 
                onClick={() => setActiveTab('today')}
                className={`rounded-2xl p-5 shadow-sm border flex items-center justify-between cursor-pointer transition-all hover:scale-[1.01] ${
                  activeTab === 'today'
                    ? (isLight ? 'bg-amber-50/80 border-amber-300 ring-2 ring-amber-200' : 'bg-amber-500/10 border-amber-500/40 ring-1 ring-amber-500/40')
                    : (isLight ? 'bg-white border-slate-200 hover:border-amber-300' : 'bg-[#0D0F17] border-white/[0.06] hover:border-amber-500/30')
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    isLight ? 'bg-amber-100 text-amber-600' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-extrabold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
                      {todayUsers.length}
                    </h3>
                    <p className={`text-xs font-semibold ${isLight ? 'text-amber-700' : 'text-amber-300'}`}>
                      Due for Call Today
                    </p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                  todayUsers.length > 0 
                    ? (isLight ? 'bg-amber-100 text-amber-700' : 'bg-amber-500/20 text-amber-300') 
                    : (isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-slate-500')
                }`}>
                  {todayUsers.length > 0 ? 'Action Needed' : 'All Clear'}
                </div>
              </div>

              {/* Upcoming Mini Card */}
              <div 
                onClick={() => setActiveTab('soon')}
                className={`rounded-2xl p-5 shadow-sm border flex items-center justify-between cursor-pointer transition-all hover:scale-[1.01] ${
                  activeTab === 'soon'
                    ? (isLight ? 'bg-cyan-50/80 border-cyan-300 ring-2 ring-cyan-200' : 'bg-cyan-500/10 border-cyan-500/40 ring-1 ring-cyan-500/40')
                    : (isLight ? 'bg-white border-slate-200 hover:border-cyan-300' : 'bg-[#0D0F17] border-white/[0.06] hover:border-cyan-500/30')
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    isLight ? 'bg-cyan-100 text-cyan-600' : 'bg-cyan-500/20 text-cyan-400'
                  }`}>
                    <CalendarDays className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-extrabold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
                      {soonUsers.length}
                    </h3>
                    <p className={`text-xs font-semibold ${isLight ? 'text-cyan-700' : 'text-cyan-300'}`}>
                      Upcoming Next 7 Days
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-bold flex items-center gap-1 ${isLight ? 'text-cyan-600' : 'text-cyan-400'}`}>
                  View <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </div>

          </div>

          {/* Main Users Directory Section */}
          <div className={`rounded-3xl shadow-sm border transition-all ${
            isLight ? 'bg-white border-slate-200' : 'bg-[#0D0F17] border-white/[0.06]'
          }`}>
            {/* Table Toolbar & Category Tabs */}
            <div className={`p-5 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 ${
              isLight ? 'border-slate-100 bg-slate-50/50' : 'border-white/[0.04] bg-white/[0.01]'
            }`}>
              
              {/* Filter Tabs */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'all'
                      ? (isLight ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-900 shadow-sm')
                      : (isLight ? 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200' : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/5')
                  }`}
                >
                  <Users className="w-3.5 h-3.5" /> All Users
                  <span className={`px-1.5 py-0.2 text-[10px] rounded-full ${
                    activeTab === 'all' ? (isLight ? 'bg-white/20 text-white' : 'bg-black/20 text-slate-900') : (isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-slate-300')
                  }`}>
                    {users.length}
                  </span>
                </button>

                <button
                  onClick={() => setActiveTab('today')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'today'
                      ? (isLight ? 'bg-amber-500 text-white shadow-sm' : 'bg-amber-500 text-black shadow-sm font-black')
                      : (isLight ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200' : 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/20')
                  }`}
                >
                  <Clock className="w-3.5 h-3.5" /> Due Today
                  <span className="px-1.5 py-0.2 text-[10px] rounded-full bg-black/15">
                    {todayUsers.length}
                  </span>
                </button>

                <button
                  onClick={() => setActiveTab('soon')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'soon'
                      ? (isLight ? 'bg-cyan-600 text-white shadow-sm' : 'bg-cyan-400 text-black shadow-sm font-black')
                      : (isLight ? 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border border-cyan-200' : 'bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 border border-cyan-500/20')
                  }`}
                >
                  <CalendarDays className="w-3.5 h-3.5" /> Upcoming
                  <span className="px-1.5 py-0.2 text-[10px] rounded-full bg-black/15">
                    {soonUsers.length}
                  </span>
                </button>

                <button
                  onClick={() => setActiveTab('overdue')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'overdue'
                      ? (isLight ? 'bg-red-600 text-white shadow-sm' : 'bg-red-500 text-white shadow-sm font-bold')
                      : (isLight ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20')
                  }`}
                >
                  <AlertCircle className="w-3.5 h-3.5" /> Overdue
                  <span className="px-1.5 py-0.2 text-[10px] rounded-full bg-black/15">
                    {overdueUsers.length}
                  </span>
                </button>

                <button
                  onClick={() => setActiveTab('unscheduled')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'unscheduled'
                      ? (isLight ? 'bg-slate-700 text-white shadow-sm' : 'bg-white/20 text-white shadow-sm')
                      : (isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200' : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/5')
                  }`}
                >
                  Unscheduled
                  <span className="px-1.5 py-0.2 text-[10px] rounded-full bg-black/15">
                    {unscheduledUsers.length}
                  </span>
                </button>
              </div>

              {/* Sort & Count Controls */}
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  Showing <strong className={isLight ? 'text-slate-800' : 'text-white'}>{displayedUsers.length}</strong> of {users.length} users
                </span>

                <div className="flex items-center gap-1.5">
                  <span className={`text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Sort:</span>
                  <select 
                    value={sortBy} 
                    onChange={e => setSortBy(e.target.value)} 
                    className={`text-xs font-semibold py-1.5 px-2.5 rounded-xl outline-none border transition-all ${
                      isLight 
                        ? 'bg-white border-slate-200 text-slate-700 focus:border-indigo-500' 
                        : 'bg-white/5 border-white/10 text-slate-200 focus:border-cyan-400'
                    }`}
                  >
                    <option value="recent">Recently Registered</option>
                    <option value="date">Follow-up Date</option>
                    <option value="name">Name (A-Z)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Users Table (Full Page View - No clipping / No max-height limit) */}
            <div className="overflow-x-auto w-full">
              <table className="w-full min-w-[900px] text-left border-collapse">
                <thead>
                  <tr className={`text-[11px] font-bold uppercase tracking-wider border-b ${
                    isLight 
                      ? 'bg-slate-50/80 text-slate-500 border-slate-200' 
                      : 'bg-white/[0.02] text-slate-400 border-white/[0.06]'
                  }`}>
                    <th className="py-3.5 pl-6 font-semibold w-[42%]">Contact Details & Notes</th>
                    <th className="py-3.5 px-4 font-semibold w-[15%]">Role / Plan</th>
                    <th className="py-3.5 px-4 font-semibold w-[23%]">Follow-up Schedule</th>
                    <th className="py-3.5 pr-6 font-semibold text-right w-[20%]">Quick Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isLight ? 'divide-slate-100' : 'divide-white/[0.04]'}`}>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="py-20 text-center">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-500 mb-3" />
                        <p className={`text-sm font-medium ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
                          Loading platform users directory...
                        </p>
                      </td>
                    </tr>
                  ) : displayedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-16 text-center">
                        <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 ${
                          isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-slate-500'
                        }`}>
                          <Search className="w-6 h-6" />
                        </div>
                        <h4 className={`text-base font-bold ${isLight ? 'text-slate-800' : 'text-white'}`}>
                          No matching users found
                        </h4>
                        <p className={`text-xs mt-1 max-w-sm mx-auto ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                          {search ? `No users found matching "${search}". Try adjusting your search query or tab filter.` : 'No contacts under this filter category.'}
                        </p>
                        {(search || activeTab !== 'all') && (
                          <Button 
                            onClick={() => { setSearch(''); setActiveTab('all'); }} 
                            variant="outline" 
                            size="sm" 
                            className="mt-4 text-xs font-semibold rounded-xl"
                          >
                            Reset Filter & Search
                          </Button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    displayedUsers.map((user, idx) => {
                      const st = user.statusInfo || getFollowupStatus(user.followup?.followup_date);
                      const cleanPhone = (user.phone || '').replace(/[^0-9+]/g, '');
                      const waNumber = cleanPhone.startsWith('+') ? cleanPhone.slice(1) : (cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone);
                      
                      return (
                        <tr 
                          key={user.user_id || user.email || idx} 
                          className={`group transition-colors ${
                            isLight ? 'hover:bg-indigo-50/40' : 'hover:bg-white/[0.02]'
                          }`}
                        >
                          {/* Contact Details Column */}
                          <td className="py-4 pl-6 pr-4">
                            <div className="flex items-start gap-3.5">
                              <Avatar className={`w-11 h-11 rounded-2xl flex-shrink-0 shadow-sm border mt-0.5 ${
                                isLight ? 'border-slate-200' : 'border-white/10'
                              }`}>
                                <AvatarImage src={user.picture} className="rounded-2xl object-cover" />
                                <AvatarFallback className={`rounded-2xl font-bold text-sm ${
                                  isLight ? 'bg-indigo-100 text-indigo-700' : 'bg-white/10 text-cyan-400'
                                }`}>
                                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                                </AvatarFallback>
                              </Avatar>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold text-sm font-outfit truncate ${
                                    isLight ? 'text-slate-900' : 'text-white'
                                  }`}>
                                    {user.name || 'Unnamed User'}
                                  </span>
                                  {user.user_id && (
                                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                      isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-slate-400'
                                    }`}>
                                      #{user.user_id.length > 10 ? user.user_id.slice(-8) : user.user_id}
                                    </span>
                                  )}
                                </div>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                                  {user.email && (
                                    <a 
                                      href={`mailto:${user.email}`} 
                                      className={`text-xs font-mono truncate hover:underline ${
                                        isLight ? 'text-slate-500 hover:text-indigo-600' : 'text-slate-400 hover:text-cyan-300'
                                      }`}
                                    >
                                      {user.email}
                                    </a>
                                  )}
                                  {user.phone && (
                                    <a 
                                      href={`tel:${cleanPhone}`} 
                                      className={`text-xs font-mono flex items-center gap-1 hover:underline ${
                                        isLight ? 'text-emerald-600' : 'text-emerald-400'
                                      }`}
                                    >
                                      <Phone className="w-3 h-3" /> {user.phone}
                                    </a>
                                  )}
                                  {user.city && (
                                    <span className={`text-xs flex items-center gap-1 ${
                                      isLight ? 'text-slate-500' : 'text-slate-400'
                                    }`}>
                                      <MapPin className="w-3 h-3 text-indigo-400" /> {user.city}
                                    </span>
                                  )}
                                </div>

                                {/* Interaction Note Callout */}
                                {user.followup?.notes && (
                                  <div className={`mt-2 p-2 rounded-xl text-xs flex items-start gap-2 max-w-md ${
                                    isLight 
                                      ? 'bg-amber-50/80 border border-amber-200/70 text-amber-900' 
                                      : 'bg-amber-500/10 border border-amber-500/20 text-amber-200'
                                  }`}>
                                    <StickyNote className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <p className="line-clamp-2 italic font-plex leading-relaxed">
                                      "{user.followup.notes}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Role Column */}
                          <td className="py-4 px-4 align-top pt-5">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full capitalize border ${getRoleBadge(user.role, isLight)}`}>
                              <Users className="w-3 h-3" />
                              {user.role || 'Student'}
                            </span>
                          </td>

                          {/* Follow-up Status Column */}
                          <td className="py-4 px-4 align-top pt-5">
                            {st ? (
                              <div className="space-y-1">
                                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full ${
                                  st.type === 'overdue' 
                                    ? (isLight ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-red-500/20 text-red-400 border border-red-500/30')
                                    : st.type === 'today'
                                    ? (isLight ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30 animate-pulse')
                                    : (isLight ? 'bg-cyan-100 text-cyan-800 border border-cyan-200' : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30')
                                }`}>
                                  {st.type === 'overdue' ? <AlertCircle className="w-3.5 h-3.5" /> : st.type === 'today' ? <Clock className="w-3.5 h-3.5" /> : <CalendarIcon className="w-3.5 h-3.5" />}
                                  {st.label}
                                </span>
                                {st.dateFormatted && (
                                  <p className={`text-[11px] font-mono pl-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {st.dateFormatted}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <button 
                                onClick={() => setSelectedUser(user)}
                                className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full border border-dashed transition-colors ${
                                  isLight 
                                    ? 'border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50' 
                                    : 'border-white/20 text-slate-400 hover:border-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10'
                                }`}
                              >
                                + Schedule
                              </button>
                            )}
                          </td>

                          {/* Quick Actions Column */}
                          <td className="py-4 pr-6 align-top pt-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {user.phone && (
                                <a 
                                  href={`https://wa.me/${waNumber}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className={`p-2 rounded-xl transition-all ${
                                    isLight 
                                      ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' 
                                      : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                                  }`}
                                  title="WhatsApp Chat"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </a>
                              )}

                              <button 
                                onClick={() => setSelectedUser(user)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all shadow-sm ${
                                  isLight 
                                    ? 'bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-700' 
                                    : 'bg-white/5 hover:bg-cyan-500 hover:text-black text-slate-200'
                                }`}
                                title="Open CRM Notes & Follow-up Scheduler"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                                <span>Manage</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer Summary */}
            <div className={`p-4 border-t flex flex-col sm:flex-row items-center justify-between text-xs ${
              isLight ? 'border-slate-100 bg-slate-50/50 text-slate-500' : 'border-white/[0.04] bg-white/[0.01] text-slate-400'
            }`}>
              <div>
                Platform Users Directory • Showing all <strong>{displayedUsers.length}</strong> active users.
              </div>
              <div className="mt-2 sm:mt-0 font-semibold text-slate-600 dark:text-slate-300">
                {users.length} total registered users
              </div>
            </div>
          </div>
          
        </div>
      </div>
      
      {/* Follow-up Slide-Over Drawer */}
      {selectedUser && (
        <FollowUpPanel 
          user={selectedUser} 
          onClose={() => setSelectedUser(null)} 
          onSave={fetchUsers} 
          theme={theme} 
        />
      )}
    </DashboardLayout>
  );
}
